import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import { adminApi } from '../../services/admin';
import extractApiError from '../../utils/extractApiError';

export default function AdminRuleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => adminApi.deleteRule(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'rules'] });
      navigate('/admin/rules');
    },
    onError: (err: unknown) => {
      setDeleteError(extractApiError(err));
    },
  });

  const { data: rule, isLoading } = useQuery({
    queryKey: ['admin', 'rules', id],
    queryFn: () => adminApi.getRule(id!),
    enabled: !!id,
  });

  const { data: versions } = useQuery({
    queryKey: ['admin', 'rule-versions', id],
    queryFn: () => adminApi.getRuleVersions(id!),
    enabled: !!id,
  });

  if (isLoading) return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>;
  if (!rule) return <div className="text-center py-12"><p className="text-sm text-slate-500">Rule not found</p></div>;

  return (
    <>
      <PageHeader
        title={rule.name}
        description={`v${rule.version} — ${rule.category}`}
        actions={
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/admin/rules/${id}/edit`)}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700"
            >
              Edit Rule
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Delete rule "${rule.name}"? This cannot be undone. Rules with trigger history cannot be deleted — deactivate them instead.`)) {
                  setDeleteError(null);
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-white border-2 border-red-200 rounded-xl hover:bg-red-50 disabled:opacity-50"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Rule'}
            </button>
            <button onClick={() => navigate('/admin/rules')} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50">
              Back to Rules
            </button>
          </div>
        }
      />

      {deleteError && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">{deleteError}</p>
        </div>
      )}

      {/* Rule Info */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Rule Configuration</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Category</dt>
            <dd className="mt-1 text-sm text-slate-900 capitalize">{rule.category}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Target Score</dt>
            <dd className="mt-1 text-sm text-slate-900 capitalize">{rule.target_score}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Rule Type</dt>
            <dd className="mt-1 text-sm text-slate-900 capitalize">{rule.rule_type}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Entity</dt>
            <dd className="mt-1 text-sm text-slate-900 capitalize">{rule.evaluation_entity}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Metric Source</dt>
            <dd className="mt-1 text-sm text-slate-900 capitalize">{rule.metric_source.replaceAll('_', ' ')}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Impact</dt>
            <dd className="mt-1">
              <span className={`font-semibold ${rule.is_positive ? 'text-emerald-600' : 'text-red-600'}`}>
                {rule.is_positive ? '+' : ''}{Number(rule.score_impact).toFixed(1)} ({rule.is_positive ? 'Reward' : 'Penalty'})
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Severity</dt>
            <dd className="mt-1">
              <StatusBadge label={rule.severity_level} variant={rule.severity_level === 'critical' ? 'error' : rule.severity_level === 'high' ? 'warning' : 'neutral'} />
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Status</dt>
            <dd className="mt-1">
              <StatusBadge label={rule.is_active ? 'Active' : 'Inactive'} variant={rule.is_active ? 'success' : 'neutral'} />
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Time Window</dt>
            <dd className="mt-1 text-sm text-slate-900">{rule.time_window_days} days</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Priority</dt>
            <dd className="mt-1 text-sm text-slate-900">{rule.priority}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Max Triggers/Cycle</dt>
            <dd className="mt-1 text-sm text-slate-900">{rule.max_triggers_per_cycle ?? 'Unlimited'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Cooldown</dt>
            <dd className="mt-1 text-sm text-slate-900">{rule.cooldown_weeks} weeks</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400 uppercase">Group</dt>
            <dd className="mt-1 text-sm text-slate-900">{rule.group || '—'}</dd>
          </div>
        </dl>
      </div>

      {/* Description */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Description</h2>
        <p className="text-sm text-slate-600">{rule.description}</p>
      </div>

      {/* Explanation Template */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Explanation Template</h2>
        <p className="text-sm text-slate-600 font-mono bg-slate-50 p-3 rounded-lg">{rule.explanation_template}</p>
      </div>

      {/* Conditions JSON */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Conditions</h2>
        <pre className="text-xs text-slate-600 bg-slate-50 p-4 rounded-lg overflow-x-auto">
          {JSON.stringify(rule.conditions, null, 2)}
        </pre>
      </div>

      {/* Version History */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Version History</h2>
        {versions && versions.length > 0 ? (
          <div className="space-y-3">
            {versions.map((v) => (
              <div key={v.id} className="border border-slate-100 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-900">Version {v.version}</span>
                  <span className="text-xs text-slate-400">{new Date(v.created_at).toLocaleString()}</span>
                </div>
                <p className="text-xs text-slate-500">
                  Impact: {Number(v.score_impact).toFixed(1)} | Window: {v.time_window_days}d
                  {v.changed_by_email && ` | By: ${v.changed_by_email}`}
                </p>
                {v.change_notes && <p className="text-xs text-slate-400 mt-1">{v.change_notes}</p>}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">No previous versions.</p>
        )}
      </div>
    </>
  );
}
